<template>
  <div class="max-w-4xl mx-auto px-6 py-16">
    <!-- Loading State (full page when searching from initial view) -->
    <div v-if="!hasSearched && isSearching" class="text-center py-32">
      <div
        class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-purple mx-auto mb-4"
      ></div>
      <p class="text-gray-500">Searching transcripts...</p>
    </div>

    <!-- Search Section (shown when no search performed) -->
    <div v-else-if="!hasSearched" class="text-center">
      <!-- Search Icon -->
      <div
        class="w-14 h-14 mx-auto mb-6 bg-primary-purple/10 rounded-xl flex items-center justify-center"
      >
        <span v-html="SEARCH_ICON" class="text-primary-purple"></span>
      </div>

      <!-- Title -->
      <h1 class="text-3xl font-bold text-gray-900 mb-8">Search Anything</h1>

      <!-- Search Bar -->
      <div class="max-w-2xl mx-auto mb-16">
        <div
          class="flex items-center bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
          :class="{
            'ring-2 ring-primary-purple border-transparent': isSearchFocused,
            'ring-2 ring-red-300 border-transparent': searchError,
          }"
        >
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            placeholder="What features are experts talking about most?"
            class="flex-1 px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none"
            @keyup.enter="performSearch"
            @focus="isSearchFocused = true"
            @blur="isSearchFocused = false"
          />
          <button
            @click="performSearch"
            :disabled="!searchQuery.trim() || isSearching"
            class="px-5 py-4 text-gray-400 hover:text-primary-purple transition-colors disabled:opacity-50"
          >
            <span v-html="SEARCH_ICON_SMALL"></span>
          </button>
        </div>
        <!-- Error message -->
        <p v-if="searchError" class="mt-2 text-sm text-red-600 text-left">
          {{ searchError }}
        </p>
      </div>

      <!-- My Watchlist Section -->
      <div class="text-left">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <span v-html="EYE_ICON" class="text-gray-600"></span>
            <h2 class="text-lg font-semibold text-gray-900">My Watchlist</h2>
          </div>
          <router-link
            :to="{ name: 'InsightsWatchlist' }"
            class="flex items-center gap-1 text-sm font-medium text-primary-purple hover:underline"
          >
            See All
            <span v-html="ARROW_RIGHT_ICON"></span>
          </router-link>
        </div>
        <p class="text-sm text-gray-500 mb-6">
          Your recent searches that you've added your library to get updates.
        </p>

        <!-- Watchlist Cards -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <WatchlistCard
            v-for="item in watchlistItems"
            :key="item.id"
            :item="item"
            :open-menu-id="openMenuId"
            @click="handleSeeAll(item)"
            @toggle-menu="openMenuId = openMenuId === $event ? null : $event"
            @view-details="
              viewDetailsItem = $event;
              openMenuId = null;
            "
            @remove="
              handleRemoveFromWatchlist($event);
              openMenuId = null;
            "
          />
        </div>

        <!-- Recent Calls Section -->
        <div class="text-left mt-12">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <span v-html="CLOCK_ICON" class="text-gray-600"></span>
              <h2 class="text-lg font-semibold text-gray-900">Recent Calls</h2>
              <span v-if="recentCalls.length > 0" class="text-sm text-gray-500">
                ({{ recentCalls.length }}{{ hasMoreRecentCalls ? "+" : "" }})
              </span>
            </div>
          </div>
          <p class="text-sm text-gray-500 mb-6">
            Browse your latest call transcripts across all programs.
          </p>

          <!-- Loading State -->
          <div
            v-if="isLoadingRecentCalls && recentCalls.length === 0"
            class="flex justify-center py-8"
          >
            <div
              class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-purple"
            ></div>
          </div>

          <!-- Recent Calls List -->
          <div v-else-if="recentCalls.length > 0" class="space-y-3">
            <div
              v-for="call in recentCalls"
              :key="call.id"
              @click="viewTranscript(call.id)"
              class="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-primary-purple/30 transition-all cursor-pointer"
            >
              <div class="flex items-start justify-between">
                <div class="flex items-start gap-3 flex-1">
                  <!-- Avatar -->
                  <div
                    class="w-8 h-8 bg-primary-purple/10 rounded flex items-center justify-center flex-shrink-0"
                  >
                    <span
                      v-html="PERSON_ICON"
                      class="w-5 h-5 text-primary-purple"
                    ></span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <h3 class="text-sm font-medium text-gray-900 truncate">
                        {{ call.callTitle || "Untitled Call" }}
                      </h3>
                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-md bg-green-100 text-green-700"
                      >
                        <span v-html="CHECK_ICON" class="w-3 h-3"></span>
                        Closed
                      </span>
                    </div>
                    <!-- Date & Attendees -->
                    <div
                      class="flex items-center gap-3 mt-1 text-xs text-gray-500"
                    >
                      <span class="flex items-center gap-1">
                        <span
                          v-html="CALENDAR_ICON"
                          class="w-3 h-3 opacity-70"
                        ></span>
                        {{ formatDate(call.processedAt) }}
                      </span>
                      <span class="flex items-center gap-1">
                        <span
                          v-html="PEOPLE_ICON"
                          class="w-3.5 h-3.5 opacity-70"
                        ></span>
                        {{ getSpeakerCount(call) }} Attendees
                      </span>
                    </div>
                    <!-- Brief Preview -->
                    <p
                      v-if="call.callBrief"
                      class="text-xs text-gray-500 mt-2 line-clamp-2"
                    >
                      {{ call.callBrief }}
                    </p>
                  </div>
                </div>
                <!-- Duration Badge -->
                <span
                  class="flex items-center gap-1 px-3 py-1 bg-primary-purple text-white text-xs font-medium rounded-full flex-shrink-0 ml-3"
                >
                  <span v-html="PLAY_ICON" class="w-3 h-3"></span>
                  {{ formatDuration(call.durationSeconds) }}
                </span>
              </div>
            </div>

            <!-- Load More Button -->
            <div v-if="hasMoreRecentCalls" class="flex justify-center pt-4">
              <button
                @click="loadMoreRecentCalls"
                :disabled="isLoadingRecentCalls"
                class="px-6 py-2 text-sm font-medium text-primary-purple border border-primary-purple rounded-lg hover:bg-primary-purple/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span
                  v-if="isLoadingRecentCalls"
                  class="flex items-center gap-2"
                >
                  <span
                    class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-purple"
                  ></span>
                  Loading...
                </span>
                <span v-else>Load More</span>
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div
            v-else
            class="text-center py-8 bg-white rounded-lg border border-gray-200"
          >
            <div
              v-html="EMPTY_SEARCH_ICON"
              class="mx-auto mb-3 text-gray-300 w-12 h-12"
            ></div>
            <p class="text-sm text-gray-500">No recent calls found</p>
          </div>
        </div>
      </div>

      <!-- Watchlist Details Modal -->
      <WatchlistDetailsModal
        :show="!!viewDetailsItem"
        :item="viewDetailsItem"
        :program-name="userStore.selectedProgram?.name ?? ''"
        @close="viewDetailsItem = null"
        @updated="viewDetailsItem = null"
      />
    </div>

    <!-- Search Results Section -->
    <div v-else>
      <!-- Search Bar (sticky at top when results shown) -->
      <div class="mb-8">
        <div
          class="flex items-center bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
          :class="{
            'ring-2 ring-primary-purple border-transparent': isSearchFocused,
          }"
        >
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            :placeholder="lastSearchQuery"
            class="flex-1 px-5 py-4 text-gray-900 placeholder-gray-400 focus:outline-none"
            @keyup.enter="performSearch"
            @focus="isSearchFocused = true"
            @blur="isSearchFocused = false"
          />
          <button
            @click="performSearch"
            :disabled="!searchQuery.trim() || isSearching"
            class="px-6 py-4 bg-primary-purple text-white font-medium hover:bg-primary-purple/90 transition-colors disabled:opacity-50"
          >
            Search
          </button>
        </div>
      </div>

      <!-- Results Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-xl font-semibold text-gray-900">
            <span class="text-primary-purple">{{ totalResults }} results</span>
            <span class="text-gray-500 font-normal">
              for "{{ lastSearchQuery }}"</span
            >
          </h2>
          <p class="text-sm text-gray-500 mt-1">
            Found in {{ searchTimeMs }}ms
          </p>
        </div>
        <div class="flex items-center gap-3">
          <button
            v-if="totalResults > 0"
            @click="showAddToWatchlistModal = true"
            class="text-sm font-medium text-primary-purple hover:underline"
          >
            Add to Watchlist
          </button>
          <button
            @click="clearResults"
            class="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Clear results
          </button>
        </div>
      </div>

      <!-- Add to Watchlist Modal -->
      <div
        v-if="showAddToWatchlistModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        @click.self="showAddToWatchlistModal = false"
      >
        <div class="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">
            Add to Watchlist
          </h3>
          <p class="text-sm text-gray-500 mb-4">
            Get updates for "{{ lastSearchQuery }}"
          </p>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Update frequency
            </label>
            <select
              v-model="addWatchlistFrequency"
              class="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div class="flex justify-end gap-2">
            <button
              @click="showAddToWatchlistModal = false"
              class="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              @click="handleAddToWatchlist"
              :disabled="addToWatchlistLoading"
              class="px-4 py-2 bg-primary-purple text-white rounded-md hover:bg-primary-purple/90 disabled:opacity-50"
            >
              {{ addToWatchlistLoading ? "Adding..." : "Add" }}
            </button>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="searchError"
        class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
      >
        {{ searchError }}
      </div>

      <!-- Loading State -->
      <div v-if="isSearching" class="flex items-center justify-center py-16">
        <div
          class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-purple"
        ></div>
      </div>

      <!-- No Results -->
      <div
        v-else-if="searchResults.length === 0"
        class="flex flex-col items-center justify-center py-16 text-center"
      >
        <div v-html="EMPTY_SEARCH_ICON" class="mb-4 text-gray-300"></div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No results found</h3>
        <p class="text-gray-500">Try adjusting your search terms</p>
      </div>

      <!-- Results List -->
      <div v-else class="space-y-4">
        <div
          v-for="result in searchResults"
          :key="result.transcriptId"
          class="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
        >
          <!-- Card Header -->
          <div class="p-4 border-b border-gray-100">
            <div class="flex items-start justify-between">
              <div class="flex items-start gap-3">
                <!-- Avatar Placeholder -->
                <div
                  class="w-8 h-8 bg-primary-purple/10 rounded flex items-center justify-center flex-shrink-0"
                >
                  <span
                    v-html="PERSON_ICON"
                    class="w-5 h-5 text-primary-purple"
                  ></span>
                </div>
                <div class="flex-1">
                  <div class="flex items-center gap-2 flex-wrap">
                    <h3 class="text-sm font-medium text-gray-900">
                      {{ result.callTitle || "Untitled Call" }}
                    </h3>
                    <!-- Status Badge -->
                    <span
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-md bg-green-100 text-green-700"
                    >
                      <span v-html="CHECK_ICON" class="w-3 h-3"></span>
                      Closed
                    </span>
                  </div>
                  <!-- Date & Attendees Row -->
                  <div
                    class="flex items-center gap-3 mt-1 text-xs text-gray-500"
                  >
                    <span class="flex items-center gap-1">
                      <span
                        v-html="CALENDAR_ICON"
                        class="w-3 h-3 opacity-70"
                      ></span>
                      {{ formatDate(result.processedAt) }}
                    </span>
                    <span class="flex items-center gap-1">
                      <span
                        v-html="PEOPLE_ICON"
                        class="w-3.5 h-3.5 opacity-70"
                      ></span>
                      {{ getAttendeeCount(result) }} Attendees
                    </span>
                  </div>
                </div>
              </div>
              <!-- Duration Badge -->
              <span
                class="flex items-center gap-1 px-3 py-1 bg-primary-purple text-white text-xs font-medium rounded-full"
              >
                <span v-html="PLAY_ICON" class="w-3 h-3"></span>
                {{ formatDuration(result.durationSeconds) }}
              </span>
            </div>
          </div>

          <!-- Call Brief Section -->
          <div class="p-4 relative">
            <div class="relative overflow-hidden max-h-[120px]">
              <p class="text-sm text-gray-600 leading-relaxed">
                <span class="font-semibold">Call Brief:</span>
                {{
                  result.callBrief ||
                  result.snippet ||
                  "No summary available for this call."
                }}
              </p>
              <!-- Fade gradient overlay -->
              <div
                class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none"
              ></div>
            </div>
            <!-- See Details Link -->
            <div class="flex justify-end mt-2">
              <button
                @click="viewTranscript(result.transcriptId)"
                class="flex items-center gap-1 text-sm font-medium text-primary-purple hover:underline"
              >
                See Details
                <span v-html="ARROW_RIGHT_ICON" class="w-4 h-4"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useQuery, useMutation } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { useInsightsStore, TranscriptSearchResult } from "@/stores/insights";
import { useUserStore } from "@/stores/user";
import WatchlistCard from "@/components/insights/WatchlistCard.vue";
import WatchlistDetailsModal from "@/components/insights/WatchlistDetailsModal.vue";

const LIST_WATCHLIST_QUERY = gql`
  query ListTranscriptWatchlist($limit: Int) {
    listTranscriptWatchlist(limit: $limit) {
      id
      query
      programId
      frequency
      addedAt
      initialResultCount
      previousResultCount
      currentResultCount
      resultsChanged
      lastRunAt
    }
  }
`;

const ADD_WATCHLIST_MUTATION = gql`
  mutation AddTranscriptWatchlist($input: AddTranscriptWatchlistInput!) {
    addTranscriptWatchlist(input: $input) {
      watchlistItem {
        id
        query
        frequency
        addedAt
        initialResultCount
        currentResultCount
        resultsChanged
      }
      errors
    }
  }
`;

const REMOVE_WATCHLIST_MUTATION = gql`
  mutation RemoveTranscriptWatchlist($input: RemoveTranscriptWatchlistInput!) {
    removeTranscriptWatchlist(input: $input) {
      success
      errors
    }
  }
`;

const SEARCH_TRANSCRIPTS_QUERY = gql`
  query SearchTranscripts($query: String!, $limit: Int, $minScore: Float) {
    searchTranscripts(query: $query, limit: $limit, minScore: $minScore) {
      results {
        transcriptId
        bookingId
        programId
        score
        relevance
        snippet
        callTitle
        callBrief
        durationSeconds
        processedAt
        expertName
        attendeeName
      }
      totalCount
      query
      searchTimeMs
    }
  }
`;

const RECENT_TRANSCRIPTS_QUERY = gql`
  query RecentTranscripts($first: Int, $after: String) {
    recentTranscripts(first: $first, after: $after) {
      nodes {
        id
        callTitle
        callBrief
        durationSeconds
        processedAt
        speakers {
          role
          name
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

// Icons
const SEARCH_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>`;

const SEARCH_ICON_SMALL = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>`;

const EYE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`;

const ARROW_RIGHT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>`;

const MORE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>`;

const PERSON_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>`;

const CHECK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>`;

const CALENDAR_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`;

const PEOPLE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;

const PLAY_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>`;

const EMPTY_SEARCH_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>`;

const CLOCK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;

interface WatchlistItem {
  id: string;
  query: string;
  programId?: string | null;
  frequency: string;
  addedAt: string;
  initialResultCount: number;
  previousResultCount?: number | null;
  currentResultCount: number;
  resultsChanged: boolean;
}

interface Speaker {
  role: string;
  name: string | null;
}

interface RecentCall {
  id: string;
  callTitle: string | null;
  callBrief: string | null;
  durationSeconds: number | null;
  processedAt: string | null;
  speakers: Speaker[];
}

export default defineComponent({
  name: "InsightsSearchView",
  components: { WatchlistCard, WatchlistDetailsModal },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const insightsStore = useInsightsStore();
    const userStore = useUserStore();

    const searchInput = ref<HTMLInputElement | null>(null);
    const searchQuery = ref("");
    const lastSearchQuery = ref("");
    const isSearchFocused = ref(false);

    // Watchlist (limit 3, results_changed first via backend sort)
    const { result: watchlistResult } = useQuery(
      LIST_WATCHLIST_QUERY,
      () => ({ limit: 3 }),
      { fetchPolicy: "network-only" }
    );
    const watchlistItems = computed(() => {
      const data = watchlistResult.value?.listTranscriptWatchlist;
      return (data || []) as WatchlistItem[];
    });

    const { mutate: addToWatchlist, loading: addToWatchlistLoading } =
      useMutation(ADD_WATCHLIST_MUTATION, {
        refetchQueries: [{ query: LIST_WATCHLIST_QUERY }],
      });
    const { mutate: removeFromWatchlist } = useMutation(
      REMOVE_WATCHLIST_MUTATION,
      {
        refetchQueries: [{ query: LIST_WATCHLIST_QUERY }],
      }
    );
    const showAddToWatchlistModal = ref(false);
    const addWatchlistFrequency = ref("weekly");
    const openMenuId = ref<string | null>(null);
    const viewDetailsItem = ref<WatchlistItem | null>(null);

    const handleAddToWatchlist = async () => {
      if (!lastSearchQuery.value.trim()) return;
      const programId = userStore.selectedProgram?.id;
      if (!programId) {
        insightsStore.setSearchError("No program selected.");
        return;
      }
      try {
        const res = await addToWatchlist({
          input: {
            query: lastSearchQuery.value,
            programId,
            frequency: addWatchlistFrequency.value,
          },
        });
        const data = (
          res as {
            data?: { addTranscriptWatchlist?: { errors?: string[] } };
          }
        )?.data?.addTranscriptWatchlist;
        if (data?.errors?.length) {
          insightsStore.setSearchError(data.errors[0]);
        }
      } finally {
        showAddToWatchlistModal.value = false;
      }
    };

    const handleRemoveFromWatchlist = async (id: string) => {
      await removeFromWatchlist({ input: { id } });
    };

    const handleSeeAll = (item: WatchlistItem) => {
      searchQuery.value = item.query;
      performSearch();
    };

    const formatWatchlistDate = (dateStr: string) => {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    const formatFrequencyLabel = (f: string) => {
      const map: Record<string, string> = {
        daily: "Daily Updates",
        weekly: "Weekly Updates",
        monthly: "Monthly Updates",
      };
      return map[f] || f;
    };

    // Reactive query variables for Apollo
    const queryVariables = ref({
      query: "",
      limit: 20,
    });
    const isQueryEnabled = ref(false);

    // Recent calls state
    const recentCalls = ref<RecentCall[]>([]);
    const hasMoreRecentCalls = ref(false);
    const endCursor = ref<string | null>(null);
    const RECENT_CALLS_LIMIT = 10;

    // Recent calls query variables
    const recentCallsVariables = ref<{ first: number; after: string | null }>({
      first: RECENT_CALLS_LIMIT,
      after: null,
    });

    // Apollo query for recent calls
    const {
      loading: recentCallsLoading,
      refetch: refetchRecentCalls,
      onResult: onRecentCallsResult,
    } = useQuery(
      RECENT_TRANSCRIPTS_QUERY,
      () => recentCallsVariables.value,
      () => ({
        fetchPolicy: "cache-and-network",
      })
    );

    // Handle recent calls results
    onRecentCallsResult((result) => {
      if (result.data?.recentTranscripts) {
        const response = result.data.recentTranscripts;
        const newCalls = response.nodes || [];

        if (recentCallsVariables.value.after === null) {
          // First page - replace
          recentCalls.value = newCalls;
        } else {
          // Load more - append
          recentCalls.value = [...recentCalls.value, ...newCalls];
        }

        hasMoreRecentCalls.value = response.pageInfo?.hasNextPage || false;
        endCursor.value = response.pageInfo?.endCursor || null;
      }
    });

    const isLoadingRecentCalls = computed(() => recentCallsLoading.value);

    const loadMoreRecentCalls = () => {
      if (!endCursor.value) return;
      recentCallsVariables.value = {
        first: RECENT_CALLS_LIMIT,
        after: endCursor.value,
      };
      refetchRecentCalls();
    };

    // Apollo query for search
    const {
      result: searchResult,
      loading: searchLoading,
      refetch: refetchSearch,
      onResult: onSearchResult,
      onError: onSearchError,
    } = useQuery(
      SEARCH_TRANSCRIPTS_QUERY,
      () => queryVariables.value,
      () => ({
        enabled: isQueryEnabled.value,
        fetchPolicy: "network-only",
      })
    );

    // Handle search results
    onSearchResult((result) => {
      if (result.data?.searchTranscripts) {
        const response = result.data.searchTranscripts;
        insightsStore.setSearchResults({
          results: response.results as TranscriptSearchResult[],
          totalCount: response.totalCount,
          query: response.query,
          searchTimeMs: response.searchTimeMs || 0,
        });
      }
      insightsStore.setSearching(false);
    });

    // Handle search errors
    onSearchError((error) => {
      console.error("Search error:", error);
      insightsStore.setSearchError(
        "Failed to perform search. Please try again."
      );
      insightsStore.setSearching(false);
    });

    // Computed from store
    const isSearching = computed(
      () => insightsStore.isSearching || searchLoading.value
    );
    const searchResults = computed(() => insightsStore.searchResults);
    const totalResults = computed(() => insightsStore.totalResults);
    const searchTimeMs = computed(() => insightsStore.searchTimeMs);
    const searchError = computed(() => insightsStore.searchError);
    const hasSearched = computed(() => insightsStore.hasSearched);

    const performSearch = async () => {
      if (!searchQuery.value.trim()) return;

      if (searchQuery.value.trim().length < 3) {
        insightsStore.setSearchError(
          "Please enter at least 3 characters to search."
        );
        return;
      }

      lastSearchQuery.value = searchQuery.value;
      insightsStore.setSearchQuery(searchQuery.value);
      insightsStore.addRecentSearch(searchQuery.value);
      insightsStore.setSearching(true);
      insightsStore.setSearchError(null);

      queryVariables.value = {
        query: searchQuery.value,
        limit: 20,
      };
      isQueryEnabled.value = true;

      if (searchResult.value) {
        refetchSearch();
      }
    };

    const clearResults = () => {
      insightsStore.clearSearch();
      searchQuery.value = "";
      lastSearchQuery.value = "";
    };

    const viewTranscript = (transcriptId: string) => {
      router.push({
        name: "InsightsTranscript",
        params: { id: transcriptId },
      });
    };

    // Formatting helpers
    const formatDuration = (seconds: number | null): string => {
      if (!seconds) return "0m";
      const mins = Math.floor(seconds / 60);
      if (mins >= 60) {
        const hours = Math.floor(mins / 60);
        const remainingMins = mins % 60;
        return `${hours}h ${remainingMins}m`;
      }
      return `${mins} mins`;
    };

    const formatDate = (dateString: string | null): string => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return (
        date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }) +
        ", " +
        date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZoneName: "short",
        })
      );
    };

    const getAttendeeCount = (result: TranscriptSearchResult): string => {
      let count = 0;
      if (result.expertName) count++;
      if (result.attendeeName) count++;
      return count > 0 ? String(count).padStart(2, "0") : "02";
    };

    const getSpeakerCount = (call: RecentCall): string => {
      const count = call.speakers?.length || 2;
      return String(count).padStart(2, "0");
    };

    const getFrequencyBadgeClass = (frequency: string): string => {
      switch (frequency) {
        case "Weekly Updates":
          return "bg-purple-100 text-purple-700 border border-purple-200";
        case "Daily Updates":
          return "bg-purple-100 text-purple-700 border border-purple-200";
        case "Monthly Updates":
          return "bg-purple-100 text-purple-700 border border-purple-200";
        default:
          return "bg-gray-100 text-gray-700";
      }
    };

    watch(
      () => route.query.q,
      (q) => {
        if (q && typeof q === "string") {
          searchQuery.value = q;
          lastSearchQuery.value = q;
          performSearch();
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      // Clear any stale search state on mount (unless we have query param)
      if (!route.query.q) {
        insightsStore.clearSearch();
      }
      searchInput.value?.focus();
    });

    return {
      // Icons
      SEARCH_ICON,
      SEARCH_ICON_SMALL,
      EYE_ICON,
      ARROW_RIGHT_ICON,
      MORE_ICON,
      PERSON_ICON,
      CHECK_ICON,
      CALENDAR_ICON,
      PEOPLE_ICON,
      PLAY_ICON,
      EMPTY_SEARCH_ICON,
      CLOCK_ICON,
      // Refs
      searchInput,
      searchQuery,
      lastSearchQuery,
      isSearchFocused,
      watchlistItems,
      showAddToWatchlistModal,
      addWatchlistFrequency,
      openMenuId,
      viewDetailsItem,
      userStore,
      addToWatchlistLoading,
      // Recent calls
      recentCalls,
      hasMoreRecentCalls,
      isLoadingRecentCalls,
      // Computed
      isSearching,
      searchResults,
      totalResults,
      searchTimeMs,
      searchError,
      hasSearched,
      // Methods
      performSearch,
      clearResults,
      viewTranscript,
      formatDuration,
      formatDate,
      getAttendeeCount,
      getSpeakerCount,
      getFrequencyBadgeClass,
      handleAddToWatchlist,
      handleRemoveFromWatchlist,
      handleSeeAll,
      formatWatchlistDate,
      formatFrequencyLabel,
      loadMoreRecentCalls,
    };
  },
});
</script>
